import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TwMainLayout } from "../../components/tailwind";
import { classNames } from "../../helpers";
import {
  Alert, Card, ClubContact, Icons, PrimaryButton, SessionSummary, TrialLayout,
  downloadIcs, mapsUrl, trialApi, useTrialClub,
} from "./trialShared";

/**
 * /trial/book/:sessionId — 1. your details  2. SMS code  3. booked.
 * POST trialRegister (texts the code)  ->  POST trialVerify (account + free credit + booking + SMS / email).
 */

const COUNTRIES = [
  ["+31", "NL"], ["+32", "BE"], ["+49", "DE"], ["+352", "LU"], ["+33", "FR"], ["+44", "UK"], ["+34", "ES"],
  ["+39", "IT"], ["+351", "PT"], ["+353", "IE"], ["+41", "CH"], ["+43", "AT"], ["+45", "DK"], ["+46", "SE"],
  ["+47", "NO"], ["+48", "PL"], ["+30", "GR"], ["+90", "TR"], ["+212", "MA"], ["+91", "IN"], ["+1", "US"],
];
/** "How did you hear about us?" (the server sends the club's list as heardFromOptions; this is the fallback). "Other"
 *  opens an optional "please specify" box; it's sent as "Other: <text>". */
const HEARD_FROM = ["Flyer", "Friends/family", "Instagram", "Google", "Other social media", "Other"];
const OTHER = "Other";
/** Dropdown arrow for the native selects (inline style: Tailwind can't build this url() class). */
const CHEVRON = {
  backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#6B6170" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>')}")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 14px center",
  backgroundSize: "16px",
};
const GENDERS = [["Female", "Female"], ["Male", "Male"], ["", "Other"]];
const STORE_KEY = "fitclub:trialForm";

const readSaved = () => {
  try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || "{}") || {}; } catch (e) { return {}; }
};
const save = (v) => {
  try { sessionStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch (e) { /* private mode */ }
};

/** A form field. `group` for buttons (chips / segmented): a <div>, since a <label> would forward taps to its first button. */
function Field({ label, error, children, hint, group = false }) {
  const Tag = group ? "div" : "label";
  return (
    <Tag className="flex flex-col gap-1.5">
      <span className="text-[14px] font-extrabold text-gray-800">{label}</span>
      {children}
      {hint && !error && <span className="text-[13px] text-gray-500">{hint}</span>}
      {error && <span className="text-[13px] font-bold text-[#B42335]">{error}</span>}
    </Tag>
  );
}
const inputCls = (err, width = "w-full") =>
  classNames(
    width,
    "h-[52px] min-w-0 rounded-2xl border bg-white px-4 text-[16px] font-semibold text-gray-900 outline-none placeholder:font-normal placeholder:text-gray-400 focus:border-brown-coffee focus:ring-2 focus:ring-[#481B3A]/15",
    err ? "border-[#E79AA4]" : "border-[#E3DCE6]"
  );

export default function TrialBookPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useParams();
  const { link, details } = useTrialClub();
  const query = window.location.search.includes("club=") ? `?club=${encodeURIComponent(link)}` : "";

  const [session, setSession] = React.useState(location.state?.session || null);
  const [apiClub, setApiClub] = React.useState(location.state?.club || null);
  const [heardOptions, setHeardOptions] = React.useState(HEARD_FROM);
  const [loadError, setLoadError] = React.useState("");

  // form | code | done
  const [step, setStep] = React.useState("form");
  const saved = React.useMemo(readSaved, []);
  const [form, setForm] = React.useState({
    fullName: saved.fullName || "",
    countryCode: saved.countryCode || "+31",
    phoneNumber: saved.phoneNumber || "",
    email: saved.email || "",
    age: saved.age || "",
    gender: saved.gender ?? null,
    heardFrom: saved.heardFrom || "",
    heardOther: saved.heardOther || "",
  });
  const [errors, setErrors] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [formError, setFormError] = React.useState("");

  const [token, setToken] = React.useState("");
  const [sentTo, setSentTo] = React.useState("");
  const [devCode, setDevCode] = React.useState("");
  const [code, setCode] = React.useState("");
  const [codeError, setCodeError] = React.useState("");
  const [resendIn, setResendIn] = React.useState(0);
  const [result, setResult] = React.useState(null);
  const codeRef = React.useRef(null);

  // Always load the session fresh (spots left can change); the state from the schedule shows it straight away.
  React.useEffect(() => {
    if (!link || !sessionId) return;
    (async () => {
      const r = await trialApi("get", "trialSchedule", { club: link, scheduleClassId: sessionId });
      if (!r.ok) { setLoadError(r.message); return; }
      setApiClub(r.data.club);
      if (Array.isArray(r.data.heardFromOptions) && r.data.heardFromOptions.length) setHeardOptions(r.data.heardFromOptions);
      if (r.data.session) setSession(r.data.session);
      else setLoadError(r.data.sessionError || "This class isn't available any more.");
    })();
  }, [link, sessionId]);

  React.useEffect(() => { window.scrollTo(0, 0); }, [step]);
  React.useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);
  React.useEffect(() => { if (step === "code") setTimeout(() => codeRef.current?.focus(), 150); }, [step]);

  const set = (k) => (e) => {
    const v = e && e.target ? e.target.value : e;
    setForm((f) => { const n = { ...f, [k]: v }; save(n); return n; });
    if (errors[k]) setErrors((x) => ({ ...x, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (form.fullName.trim().length < 2) e.fullName = "Please enter your full name.";
    if (form.phoneNumber.replace(/\D/g, "").length < 6) e.phoneNumber = "Please enter your mobile number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) e.email = "Please enter a valid email address.";
    const age = parseInt(form.age, 10);
    if (!(age >= 12 && age <= 99)) e.age = "Enter your age.";
    if (form.gender === null) e.gender = "Please choose one.";
    if (!form.heardFrom) e.heardFrom = "Please choose one.";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const sendCode = async (ev) => {
    ev && ev.preventDefault();
    setFormError("");
    if (!validate()) {
      setTimeout(() => document.querySelector("[data-invalid='true']")?.scrollIntoView({ block: "center", behavior: "smooth" }), 50);
      return;
    }
    setBusy(true);
    const r = await trialApi("post", "trialRegister", {
      club: link,
      scheduleClassId: sessionId,
      ...form,
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      gender: form.gender || "",
      heardFrom: form.heardFrom === OTHER && form.heardOther.trim() ? `${OTHER}: ${form.heardOther.trim()}` : form.heardFrom,
      heardOther: undefined,
    });
    setBusy(false);
    if (!r.ok) { setFormError(r.message); return; }
    setToken(r.data.token);
    setSentTo(r.data.sentTo || `${form.countryCode} ${form.phoneNumber}`);
    setDevCode(r.data.code ? String(r.data.code) : "");
    setCode("");
    setCodeError("");
    setResendIn(30);
    setStep("code");
  };

  const verify = async (value) => {
    const c = String(value ?? code).replace(/\D/g, "");
    if (c.length < 4 || busy) return;
    setBusy(true);
    setCodeError("");
    const r = await trialApi("post", "trialVerify", { token, code: c });
    setBusy(false);
    if (!r.ok) { setCodeError(r.message); return; }
    setResult(r.data);
    if (r.data?.session) setSession(r.data.session);
    try { sessionStorage.removeItem(STORE_KEY); } catch (e) { /* ignore */ }
    setStep("done");
  };

  const clubName = apiClub?.name || details?.business_detail?.businessName || "the club";
  const first = form.fullName.trim().split(/\s+/)[0] || "";

  return (
    <TwMainLayout fullScreen={true} checkSession={false} userType="skip">
      <TrialLayout details={details} apiClub={apiClub} compact>
        <div className="flex flex-col gap-5 pt-4">
          {step !== "done" && (
            <button
              type="button"
              onClick={() => (step === "code" ? setStep("form") : navigate(`/trial${query}`))}
              className="-ml-1 flex w-fit items-center gap-1 text-[15px] font-extrabold text-brown-coffee"
            >
              {Icons.back("h-5 w-5")} {step === "code" ? "Change my details" : "All classes"}
            </button>
          )}

          {loadError && step === "form" ? (
            <Card className="flex flex-col gap-4 p-5">
              <Alert>{loadError}</Alert>
              <PrimaryButton onClick={() => navigate(`/trial${query}`)}>Pick another class</PrimaryButton>
            </Card>
          ) : (
            <>
              {step !== "done" && session && <SessionSummary session={session} />}
              {step !== "done" && !session && <div className="h-[132px] animate-pulse rounded-3xl bg-[#EDE6F0]" />}

              {step === "form" && (
                <form className="flex flex-col gap-5" onSubmit={sendCode} noValidate>
                  <div>
                    <h1 className="text-[24px] font-black tracking-tight text-gray-900">Your details</h1>
                    <p className="mt-1 text-[15px] text-gray-600">Free, no payment. We'll text you a code to confirm your spot.</p>
                  </div>

                  <div data-invalid={!!errors.fullName}>
                    <Field label="Full name" error={errors.fullName}>
                      <input className={inputCls(errors.fullName)} value={form.fullName} onChange={set("fullName")} autoComplete="name" autoCapitalize="words" placeholder="First and last name" enterKeyHint="next" />
                    </Field>
                  </div>

                  <div data-invalid={!!errors.phoneNumber}>
                    <Field group label="Mobile number" error={errors.phoneNumber} hint="We text your code and booking details here.">
                      <div className="flex gap-2">
                        <select
                          aria-label="Country code"
                          value={form.countryCode}
                          onChange={set("countryCode")}
                          style={CHEVRON}
                          className={classNames(inputCls(false, "w-[108px]"), "shrink-0 appearance-none pr-8")}
                        >
                          {COUNTRIES.map(([cc, iso]) => <option key={cc} value={cc}>{iso} {cc}</option>)}
                        </select>
                        <input
                          className={inputCls(errors.phoneNumber)}
                          value={form.phoneNumber}
                          onChange={set("phoneNumber")}
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel-national"
                          placeholder="6 12345678"
                          enterKeyHint="next"
                        />
                      </div>
                    </Field>
                  </div>

                  <div data-invalid={!!errors.email}>
                    <Field label="Email" error={errors.email}>
                      <input className={inputCls(errors.email)} value={form.email} onChange={set("email")} type="email" inputMode="email" autoComplete="email" autoCapitalize="none" placeholder="you@example.com" enterKeyHint="next" />
                    </Field>
                  </div>

                  <div className="grid grid-cols-[96px_1fr] gap-3" data-invalid={!!(errors.age || errors.gender)}>
                    <Field label="Age" error={errors.age}>
                      <input className={inputCls(errors.age)} value={form.age} onChange={(e) => set("age")(e.target.value.replace(/\D/g, "").slice(0, 2))} inputMode="numeric" autoComplete="off" placeholder="30" />
                    </Field>
                    <Field group label="Gender" error={errors.gender}>
                      <div className="grid h-[52px] grid-cols-3 gap-1 rounded-2xl border border-[#E3DCE6] bg-white p-1" role="radiogroup" aria-label="Gender">
                        {GENDERS.map(([value, label]) => (
                          <button
                            key={label}
                            type="button"
                            role="radio"
                            aria-checked={form.gender === value}
                            onClick={() => set("gender")(value)}
                            className={classNames("rounded-xl text-[14px] font-extrabold", form.gender === value ? "bg-brown-coffee text-white" : "text-gray-700")}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </Field>
                  </div>

                  <div data-invalid={!!errors.heardFrom} className="flex flex-col gap-3">
                    <Field label="How did you hear about us?" error={errors.heardFrom}>
                      <select
                        value={form.heardFrom}
                        onChange={set("heardFrom")}
                        style={CHEVRON}
                        className={classNames(inputCls(errors.heardFrom), "appearance-none pr-11", form.heardFrom ? "" : "font-normal text-gray-400")}
                      >
                        <option value="" disabled>Choose one</option>
                        {heardOptions.map((h) => <option key={h} value={h} className="font-semibold text-gray-900">{h}</option>)}
                      </select>
                    </Field>
                    {form.heardFrom === OTHER && (
                      <Field label={<>Please specify <span className="font-semibold text-gray-500">(optional)</span></>}>
                        <input className={inputCls(false)} value={form.heardOther} onChange={set("heardOther")} maxLength={50} placeholder="e.g. walked past, an event" autoComplete="off" />
                      </Field>
                    )}
                  </div>

                  <Alert>{formError}</Alert>
                  <PrimaryButton type="submit" busy={busy} tone="coral" disabled={!session}>
                    Register
                  </PrimaryButton>
                  <p className="-mt-2 text-center text-[13px] leading-relaxed text-gray-500">
                    Next, we'll text you a 4-digit code to verify your mobile number.
                    <br />
                    By registering you agree that {clubName} contacts you about your trial.
                  </p>
                </form>
              )}

              {step === "code" && (
                <form className="flex flex-col gap-5" onSubmit={(e) => { e.preventDefault(); verify(); }} noValidate>
                  <div>
                    <h1 className="text-[24px] font-black tracking-tight text-gray-900">Enter your code</h1>
                    <p className="mt-1 text-[15px] text-gray-600">
                      We texted a 4-digit code to <b className="text-gray-900">{sentTo}</b>.
                    </p>
                  </div>
                  <input
                    ref={codeRef}
                    value={code}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "").slice(0, 4);
                      setCode(v);
                      setCodeError("");
                      if (v.length === 4) verify(v);
                    }}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    aria-label="4-digit code"
                    placeholder="••••"
                    className={classNames(
                      "h-[68px] w-full rounded-2xl border bg-white text-center text-[32px] font-black tracking-[0.6em] text-gray-900 outline-none focus:border-brown-coffee focus:ring-2 focus:ring-[#481B3A]/15",
                      codeError ? "border-[#E79AA4]" : "border-[#E3DCE6]"
                    )}
                  />
                  {devCode && <p className="-mt-3 text-center text-[13px] text-gray-500">Test server: your code is {devCode}</p>}
                  <Alert>{codeError}</Alert>
                  <PrimaryButton type="submit" busy={busy} disabled={code.length < 4} tone="coral">
                    Confirm my free class
                  </PrimaryButton>
                  <div className="flex items-center justify-between text-[14px] font-bold">
                    <button type="button" onClick={() => setStep("form")} className="text-gray-600">Wrong number?</button>
                    <button type="button" disabled={resendIn > 0 || busy} onClick={sendCode} className="text-brown-coffee disabled:text-gray-400">
                      {resendIn > 0 ? `Send again in ${resendIn}s` : "Send the code again"}
                    </button>
                  </div>
                </form>
              )}

              {step === "done" && session && (
                <div className="flex flex-col gap-5 pt-2">
                  <div className="flex flex-col items-center text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#E8F7EE] text-[#1F6B3A]">{Icons.check("h-8 w-8")}</div>
                    <h1 className="mt-4 text-[26px] font-black tracking-tight text-gray-900">
                      {first ? `You're booked, ${first}!` : "You're booked!"}
                    </h1>
                    <p className="mt-1 text-[15px] text-gray-600">See you at {clubName}. Come 10 minutes early and bring water.</p>
                  </div>
                  <SessionSummary session={session} />
                  <div className="grid grid-cols-2 gap-3">
                    <PrimaryButton tone="soft" onClick={() => downloadIcs(session, clubName)} className="whitespace-nowrap text-[14px]">
                      {Icons.cal("h-5 w-5")} Add to calendar
                    </PrimaryButton>
                    {mapsUrl(session.location) ? (
                      <a href={mapsUrl(session.location)} target="_blank" rel="noreferrer" className="flex h-[54px] items-center justify-center gap-2 rounded-2xl bg-[#F3ECF5] text-[15px] font-extrabold text-brown-coffee">
                        {Icons.pin("h-5 w-5")} Directions
                      </a>
                    ) : <span />}
                  </div>
                  <Card className="p-4 text-[14px] leading-relaxed text-gray-700">
                    We've texted the details to <b className="text-gray-900">{result?.sentTo || sentTo}</b>, with a link to change or cancel
                    {result?.email ? <> (also sent to {result.email})</> : null}. Changes are possible up to 3 hours before the class.
                    {result?.manageUrl && (
                      <a href={result.manageUrl} className="mt-2 block break-all text-[13px] font-bold text-brown-coffee">Test server: open the change / cancel page</a>
                    )}
                  </Card>
                  {apiClub && (apiClub.workNumber || apiClub.workEmail) && (
                    <div className="flex flex-col gap-2 px-1">
                      <div className="text-[13px] font-extrabold uppercase tracking-wide text-gray-500">Questions?</div>
                      <ClubContact club={apiClub} />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </TrialLayout>
    </TwMainLayout>
  );
}
